// The calendar engine — pure. One timeline, two layers: PLAN (events, project blocks, timed tasks, the
// anytime list) and ACTUAL (clock sessions, task segments, breaks, drives, distracting-phone stretches —
// derived from data already recorded, never typed). Screens only draw what comes out of here.
import { blockMinutes } from './commands/blocks.js';
import { datesBetween } from './jobplan.js';
import { localDateStr } from './time.js';
import { daySessions } from './phonestats.js';
import { carriedDays } from './dayplan.js';

export const SNAP_MIN = 15;
const DAY_MS = 86400000;
const midnight = (date) => new Date(`${date}T00:00:00`).getTime();
const minOf = (ms, date) => Math.round((ms - midnight(date)) / 60000);
const hhmmToMin = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const dow = (date) => new Date(`${date}T12:00:00`).getDay();

/** A task's planned start in minutes: the real field, else the legacy scheduledTime ("9", "2pm", "4:30 AM") / timeBlock bucket. */
export function plannedStartOf(task) {
  if (!task) return null;
  if (Number.isFinite(task.plannedStart)) return task.plannedStart;
  if (task.plannedStart === null && task.plannedStart !== undefined) return null;
  const st = String(task.scheduledTime || '').trim();
  if (st) {
    const m = st.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i);
    if (m) {
      let h = parseInt(m[1], 10); const mi = m[2] ? parseInt(m[2], 10) : 0; const ap = (m[3] || '').toLowerCase().replace(/\./g, '');
      if (ap === 'pm' && h < 12) h += 12; if (ap === 'am' && h === 12) h = 0;
      if (h >= 0 && h < 24) return h * 60 + mi;
    }
  }
  if (task.timeBlock === 'morning') return 9 * 60;
  if (task.timeBlock === 'afternoon') return 14 * 60;
  if (task.timeBlock === 'evening') return 19 * 60;
  return null;
}

/** Occurrences of repeating events / blocks inside [from, to] (dates). Non-repeating items pass through if in range. */
export function expandRepeats(items = [], from, to) {
  const out = [];
  for (const it of items) {
    if (!it?.date) continue;
    const r = it.repeat;
    if (!r || !r.freq) { if (it.date >= from && it.date <= to) out.push(it); continue; }
    const every = Math.max(1, Number(r.every) || 1);
    const until = r.until && r.until < to ? r.until : to;
    if (it.date > until) continue;
    const start = it.date < from ? it.date : it.date;
    for (const d of datesBetween(start, until)) {
      if (d < from) continue;
      const days = Math.round((midnight(d) - midnight(it.date)) / DAY_MS);
      let hit = false;
      if (r.freq === 'daily') hit = days % every === 0;
      else if (r.freq === 'weekly') hit = (Array.isArray(r.days) && r.days.length ? r.days.includes(dow(d)) : dow(d) === dow(it.date)) && Math.floor(days / 7) % every === 0;
      else if (r.freq === 'monthly') { const a = new Date(`${it.date}T12:00:00`), b = new Date(`${d}T12:00:00`); hit = b.getDate() === a.getDate() && ((b.getFullYear() - a.getFullYear()) * 12 + b.getMonth() - a.getMonth()) % every === 0; }
      if (!hit) continue;
      out.push(d === it.date ? it : { ...it, date: d, occurrenceOf: it.id, id: `${it.id}@${d}`, ...(it.start && it.end && it.start > 1440 ? { start: it.start + days * DAY_MS, end: it.end + days * DAY_MS } : {}) });
    }
  }
  return out;
}

/**
 * The PLAN layer for one day. `dayPlan` = selectDayPlan() output (the rolling plan) so the anytime strip
 * is exactly what the app calls today's plan; timed tasks come out of it into the grid.
 * Items: { id, type: 'event'|'block'|'task', title, start, end (minutes), kind, projectId, departmentId, color?, ref }.
 */
export function planItems({ date, events = [], blocks = [], tasks = [], dayPlan = null, today = null }) {
  const timed = [];
  for (const e of expandRepeats(events, date, date)) {
    if (e.status === 'cancelled') continue;
    const start = e.allDay || !e.start ? null : minOf(e.start, date);
    const end = e.allDay || !e.end ? null : minOf(e.end, date);
    timed.push({ id: e.id, type: 'event', title: e.title, kind: e.kind || 'other', start, end: end != null && start != null && end <= start ? start + 30 : end, allDay: !!e.allDay || start == null, projectId: e.projectId || null, departmentId: e.departmentId || null, customerName: e.customerName || null, status: e.status || 'planned', ref: e });
  }
  for (const b of expandRepeats(blocks.filter((b) => b.date === date || b.repeat), date, date)) {
    const [start, end] = blockMinutes(b);
    timed.push({ id: b.id, type: 'block', title: b.title || '', kind: b.kind || 'work', start, end, projectId: b.projectId || null, departmentId: b.departmentId || null, color: b.color || null, ref: b });
  }
  const t0 = today || date;
  const plan = dayPlan || tasks.filter((t) => !t.parentId && t.status === 'todo' && !t.bucket && t.scheduledDate === date);
  const anytime = [];
  for (const t of plan) {
    const ps = plannedStartOf(t);
    if (ps != null && (t.scheduledDate === date || (!t.scheduledDate && date === t0))) {
      const len = Math.max(15, t.estimatedMinutes || 30);
      timed.push({ id: t.id, type: 'task', title: t.title, start: ps, end: Math.min(1440, ps + len), projectId: t.projectId || null, estimatedMinutes: t.estimatedMinutes || null, ref: t });
    } else anytime.push(Object.assign(t, {}));
  }
  // Overdue = dated before this day and NOT already carried into the plan (the strip shows those with "Nd").
  const inPlan = new Set([...timed.map((i) => i.id), ...anytime.map((t) => t.id)]);
  const due = tasks.filter((t) => t.status === 'todo' && !t.bucket && !t.parentId && t.scheduledDate && t.scheduledDate < date && !inPlan.has(t.id)).map((t) => ({ id: t.id, title: t.title, projectId: t.projectId || null, carried: carriedDays(t, date), ref: t }));
  timed.sort((a, b) => (a.start ?? -1) - (b.start ?? -1) || (a.end ?? 0) - (b.end ?? 0));
  return { timed, anytime, due };
}

/**
 * The ACTUAL layer for one day, in minutes on the same grid.
 * { id, type: 'clock'|'segment'|'break'|'drive'|'phone', start, end, projectId?, taskId?, title, km?, ref }
 */
export function actualItems({ date, sessions = [], tasks = [], phone = null, distract = null, now = Date.now(), minPhoneMs = 5 * 60000 }) {
  const day0 = midnight(date); const day1 = day0 + DAY_MS;
  const clip = (a, b) => [Math.max(a, day0), Math.min(b, day1, now)];
  const out = [];
  for (const s of sessions) {
    if (s.migrated || !s.startedAt) continue;
    const [a, b] = clip(s.startedAt, s.endedAt || now);
    if (!(b > a)) continue;
    if (s.type === 'travel') out.push({ id: s.id, type: 'drive', start: minOf(a, date), end: minOf(b, date), title: 'Drive', km: s.distanceM ? Math.round(s.distanceM / 100) / 10 : null, open: !s.endedAt, ref: s });
    else out.push({ id: s.id, type: 'clock', start: minOf(a, date), end: minOf(b, date), projectId: s.projectId || null, departmentId: s.departmentId || null, title: 'On the clock', open: !s.endedAt, phantom: !s.projectId && !(s.focusedTime > 0), ref: s });
  }
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (!e || e.date !== date || !(e.duration > 0) || !e.startTime) continue;
      const start = hhmmToMin(e.startTime);
      const end = e.endTime ? hhmmToMin(e.endTime) : start + Math.round(e.duration / 60000);
      out.push({ id: `${t.id}:${e.id}`, type: 'segment', start, end: end > start ? end : start + Math.round(e.duration / 60000), projectId: t.projectId || null, taskId: t.id, title: t.title, manual: !!e.manual, ref: e });
    }
    for (const l of t.statusLog || []) {
      if (!(l?.duration > 0) || !l.startedAt || localDateStr(l.startedAt) !== date) continue;
      out.push({ id: `${t.id}:b${l.startedAt}`, type: 'break', start: minOf(l.startedAt, date), end: minOf(l.endedAt || l.startedAt + l.duration, date), taskId: t.id, title: l.context || l.type || 'Break', ref: l });
    }
  }
  if (phone && distract && distract.size) {
    const stretches = daySessions(phone).filter((s) => distract.has(s.pkg)).sort((a, b) => a.start - b.start);
    let cur = null;
    const flush = () => { if (cur && cur.end - cur.start >= minPhoneMs) out.push({ id: `phone:${cur.start}`, type: 'phone', start: minOf(cur.start, date), end: minOf(cur.end, date), title: cur.labels.slice(0, 2).join(', '), ref: cur }); cur = null; };
    for (const s of stretches) {
      if (cur && s.start - cur.end < 2 * 60000) { cur.end = Math.max(cur.end, s.end); if (!cur.labels.includes(s.label)) cur.labels.push(s.label); }
      else { flush(); cur = { start: s.start, end: s.end, labels: [s.label] }; }
    }
    flush();
  }
  out.sort((a, b) => a.start - b.start || a.end - b.end);
  return out;
}

/** Overlap packing: each item gets lane / lanes for side-by-side drawing. Items with start == null are skipped. */
export function layoutColumns(items = []) {
  const timed = items.filter((i) => i.start != null && i.end != null).sort((a, b) => a.start - b.start || b.end - a.end);
  const out = new Map();
  let cluster = []; let clusterEnd = -1;
  const place = () => {
    const lanes = [];
    for (const it of cluster) {
      let lane = lanes.findIndex((end) => end <= it.start);
      if (lane < 0) { lane = lanes.length; lanes.push(it.end); } else lanes[lane] = it.end;
      out.set(it.id, { lane });
    }
    for (const it of cluster) out.get(it.id).lanes = lanes.length;
    cluster = [];
  };
  for (const it of timed) {
    if (cluster.length && it.start >= clusterEnd) place();
    cluster.push(it); clusterEnd = Math.max(clusterEnd, it.end);
  }
  if (cluster.length) place();
  return items.map((i) => ({ ...i, ...(out.get(i.id) || { lane: 0, lanes: 1 }) }));
}

/** Hours to draw: earliest item − 1 h … latest + 1 h, never narrower than 7 AM–6 PM, whole hours, within the day. */
export function fitHours(items = [], { min = 7, max = 18, nowMin = null } = {}) {
  let lo = min * 60, hi = max * 60;
  for (const i of items) { if (i.start != null) lo = Math.min(lo, i.start - 60); if (i.end != null) hi = Math.max(hi, i.end + 60); }
  if (nowMin != null) { lo = Math.min(lo, nowMin - 60); hi = Math.max(hi, nowMin + 60); }
  return { startHour: Math.max(0, Math.floor(lo / 60)), endHour: Math.min(24, Math.ceil(hi / 60)) };
}

/**
 * Fill the open time around fixed items with the anytime tasks, in plan order, by estimate (default 30 min),
 * on 15-min slots, between `from` and `to` (minutes; default now-or-8 AM … 6 PM). A suggestion: nothing moves
 * until the caller writes plannedStart.
 */
export function autoPlan({ fixed = [], tasks = [], from = 8 * 60, to = 18 * 60, gapMin = 0, nowMin = null }) {
  const busy = fixed.filter((i) => i.start != null && i.end != null).map((i) => [i.start, i.end]).sort((a, b) => a[0] - b[0]);
  let cursor = Math.max(from, nowMin != null ? Math.ceil(nowMin / SNAP_MIN) * SNAP_MIN : from);
  const placed = []; const unplaced = [];
  for (const t of tasks) {
    const len = Math.max(SNAP_MIN, Math.ceil((t.estimatedMinutes || 30) / SNAP_MIN) * SNAP_MIN);
    let start = cursor; let ok = false;
    for (let guard = 0; guard < 200 && start + len <= to; guard++) {
      const clash = busy.find(([a, b]) => start < b + gapMin && start + len > a - gapMin);
      if (!clash) { ok = true; break; }
      start = Math.ceil((clash[1] + gapMin) / SNAP_MIN) * SNAP_MIN;
    }
    if (!ok) { unplaced.push(t); continue; }
    placed.push({ id: t.id, title: t.title, start, end: start + len, projectId: t.projectId || null, estimatedMinutes: t.estimatedMinutes || null });
    busy.push([start, start + len]); busy.sort((a, b) => a[0] - b[0]);
    cursor = start + len + gapMin;
    cursor = Math.ceil(cursor / SNAP_MIN) * SNAP_MIN;
  }
  return { placed, unplaced };
}

/** The next days: events, job days / blocks, due tasks — grouped by day, for Home, the Agenda view and the MCP. */
export function agenda({ from, to, events = [], blocks = [], tasks = [], today = from }) {
  const days = new Map();
  const day = (d) => { if (!days.has(d)) days.set(d, { date: d, events: [], blocks: [], due: [] }); return days.get(d); };
  for (const e of expandRepeats(events, from, to)) if (e.status !== 'cancelled') day(e.date).events.push(e);
  for (const b of expandRepeats(blocks, from, to)) day(b.date).blocks.push(b);
  for (const t of tasks) {
    if (t.status !== 'todo' || t.bucket || t.parentId || !t.scheduledDate) continue;
    if (t.scheduledDate < today && today >= from && today <= to) day(today).due.push({ ...t, overdue: true });
    else if (t.scheduledDate >= from && t.scheduledDate <= to) day(t.scheduledDate).due.push(t);
  }
  return [...days.values()].sort((a, b) => a.date.localeCompare(b.date)).map((d) => ({
    ...d,
    events: d.events.sort((a, b) => (a.allDay ? -1 : a.start || 0) - (b.allDay ? -1 : b.start || 0)),
    blocks: d.blocks.sort((a, b) => blockMinutes(a)[0] - blockMinutes(b)[0]),
  }));
}

/** Planned vs clocked minutes per project for the Daily Log — information, never a score. */
export function planVsActual({ plan = [], actual = [] }) {
  const by = new Map();
  const row = (pid) => { const k = pid || 'none'; if (!by.has(k)) by.set(k, { projectId: pid || null, plannedMin: 0, clockedMin: 0 }); return by.get(k); };
  for (const p of plan) if (p.type === 'block' && p.start != null) row(p.projectId).plannedMin += p.end - p.start;
  for (const a of actual) if (a.type === 'clock' && !a.phantom) row(a.projectId).clockedMin += a.end - a.start;
  const events = plan.filter((p) => p.type === 'event');
  return {
    byProject: [...by.values()].sort((a, b) => b.plannedMin + b.clockedMin - (a.plannedMin + a.clockedMin)),
    plannedMin: [...by.values()].reduce((s, r) => s + r.plannedMin, 0),
    clockedMin: [...by.values()].reduce((s, r) => s + r.clockedMin, 0),
    eventsDone: events.filter((e) => e.status === 'done').length,
    eventsMissed: events.filter((e) => e.status === 'no-show').length,
    eventsPlanned: events.filter((e) => e.status === 'planned').length,
  };
}
